import { Building2, Landmark, Globe, Scale } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';
import ExpertiseSection from "./ExpertiseSection";
import StructuredData from "./StructuredData";

export default function NosServices() {
  // eslint-disable-next-line no-unused-vars
  const services = [
    {
      id: "finance-bancaire",
      icon: <Building2 className="w-16 h-16 text-[#0B2545]" strokeWidth={1} />,
      title: "Finance bancaire",
      subtitle: "Votre partenaire de confiance pour toutes vos opérations bancaires",
      description: "Nous vous accompagnons dans la gestion optimale de vos relations bancaires et dans le montage de vos dossiers de financement, que ce soit en dinars ou en devises.",
      features: [
        "Entrée en relation avec la banque et les salles de marché",
        "Négociation des tarifs bancaires",
        "Montage des dossiers de crédit et financement en dinars et en devises",
        "Gestion de la trésorerie",
        "Conformité réglementaire et audit",
        "Structuration de financements et analyse de crédit"
      ],
      benefits: [
        "Accompagnement personnalisé",
        "Optimisation de vos coûts de financement",
        "Sécurisation de vos opérations bancaires"
      ]
    },
    {
      id: "finance-islamique",
      icon: <Landmark className="w-16 h-16 text-[#0B2545]" strokeWidth={1} />,
      title: "Finance islamique",
      subtitle: "Solutions financières conformes aux principes de la charia",
      description: "Notre expertise en finance islamique vous permet d'accéder à des instruments financiers éthiques et conformes aux principes de la charia, adaptés à vos besoins.",
      features: [
        "Conseil sur les instruments financiers conformes à la charia",
        "Structuration de produits islamiques (Mourabaha, Moucharaka, Ijara, Sukuk...)",
        "Développement de partenariats avec des institutions spécialisées",
        "Audit de conformité Sharia",
        "Formation et sensibilisation",
        "Conseil stratégique en finance participative"
      ],
      benefits: [
        "Respect des principes éthiques islamiques",
        "Diversification de vos sources de financement",
        "Accès à un marché en forte croissance"
      ]
    },
    {
      id: "paiements-internationaux",
      icon: <Globe className="w-16 h-16 text-[#0B2545]" strokeWidth={1} />,
      title: "Paiements internationaux & Incoterms",
      subtitle: "Maîtrisez vos opérations de commerce international",
      description: "Nous vous accompagnons dans toutes vos opérations de commerce extérieur en optimisant vos flux de paiements internationaux et en sécurisant vos transactions.",
      features: [
        "Crédit documentaire et lettre de crédit",
        "Encaissement documentaire et remise documentaire",
        "Virement et transferts internationaux",
        "Réglementation des changes et gestion des risques de change",
        "Choix et maîtrise des Incoterms",
        "Gestion des garanties bancaires internationales"
      ],
      benefits: [
        "Accompagnement personnalisé",
        "Obtention des autorisations",
        "Conformité réglementaire"
      ]
    },
    {
      id: "arbitrage-mediation",
      icon: <Scale className="w-16 h-16 text-[#0B2545]" strokeWidth={1} />,
      title: "Arbitrage et médiation",
      subtitle: "Résolution efficace de vos conflits commerciaux et financiers",
      description: "Notre équipe d'experts vous accompagne dans la résolution amiable ou arbitrale de vos litiges bancaires, commerciaux et financiers, en privilégiant des solutions rapides et équitables.",
      features: [
        "Arbitrage bancaire et financier",
        "Médiation conventionnelle et commerciale",
        "Conflits commerciaux et contractuels",
        "Transport et logistique",
        "Partenariats internationaux",
        "Litiges liés aux projets d'investissement"
      ],
      benefits: [
        "Résolution rapide des conflits",
        "Confidentialité garantie",
        "Économie de temps et d'argent",
        "Préservation des relations d'affaires"
      ]
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Conseil financier",
    "provider": {
      "@type": "Organization",
      "name": "Bankelys",
      "url": "https://bankelys.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Tunisia"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services Bankelys",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Finance Bancaire",
            "description": "Entrée en relation avec la banque, négociation des tarifs bancaires, crédit, financement, gestion de la trésorerie et conformité réglementaire."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Finance Islamique",
            "description": "Conseil en finance islamique : Mourabaha, Moucharaka, Ijara, Sukuk. Instruments financiers conformes à la charia."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Paiements Internationaux & Incoterms",
            "description": "Crédit documentaire, lettre de crédit, encaissement documentaire, remise documentaire, virement, réglementation des changes, Incoterms."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Arbitrage et Médiation Financier",
            "description": "Arbitrage et médiation : résolution de litiges bancaires, financiers et commerciaux. Médiation conventionnelle."
          }
        }
      ]
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://bankelys.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Nos Services",
        "item": "https://bankelys.com/services"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Nos Services — Finance, Arbitrage & Médiation | Bankelys</title>
        <meta name="description" content="Crédit, financement, Mourabaha, crédit documentaire, transferts internationaux, arbitrage et médiation. Services Bankelys à Tunis." />
        <link rel="canonical" href="https://bankelys.com/services" />
        <meta property="og:title" content="Services de Conseil Financier — Bankelys Tunis" />
        <meta property="og:description" content="Finance bancaire, finance islamique, paiements internationaux, arbitrage et médiation. Des solutions adaptées à vos besoins." />
        <meta property="og:url" content="https://bankelys.com/services" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Nos Services — Finance & Arbitrage | Bankelys" />
        <meta name="twitter:description" content="Finance bancaire, islamique, commerce international, arbitrage et médiation. Solutions sur mesure à Tunis." />
      </Helmet>
      <StructuredData data={serviceData} />
      <StructuredData data={breadcrumbData} />

      {/* Hero Section with Image Background - Reduced Height */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/images/slide7.png"
            alt="Services de conseil financier Bankelys — finance bancaire, islamique et arbitrage"
            className="w-full h-full object-cover"
          />
          {/* Dark Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto text-center px-6 md:px-10">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-light mb-4 leading-tight text-white animate-fade-in-up">
            Des solutions adaptées à vos besoins
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed animate-fade-in-up-delay">
            Bankelys met à votre disposition une expertise approfondie couvrant tous les aspects de la finance moderne et des opérations internationales 
          </p>
          
        </div>
      </section>
      <ExpertiseSection />

      {/* CTA Section */}
      <section className="bg-white py-24 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <div
            className="bg-[#0B2545] text-white py-14 px-10 sm:px-14 rounded-2xl 
                       flex flex-col md:flex-row items-center justify-between 
                       text-center md:text-left shadow-sm"
          >
            <p className="text-[22px] md:text-[26px] font-light mb-6 md:mb-0 max-w-2xl leading-[1.5]">
              Prêt à démarrer votre projet ? Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment nous pouvons vous accompagner.
            </p>
            <Link
              to="/contact"
              className="bg-[#B91C1C] hover:bg-[#991b1b] text-white font-medium text-[15px]
                         px-11 py-4 rounded-full transition-all duration-300 
                         hover:shadow-lg whitespace-nowrap inline-block"
            >
              Prendre contact
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}